import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AddressInput, type AddressData } from '@/components/ui/address-input';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  updateGasStation,
  deleteGasStation,
  getAddress,
  getLogoUploadUrl,
  confirmLogoUpload,
  deleteGasStationLogo,
  type ErrorResponse,
} from '@vn-propane/shared/client';
import { useGasStation } from '../GasStationsLayout';
import { Upload, X, ImageIcon, Loader2 } from 'lucide-react';
import { getImageUrl } from '@/lib/utils';

export default function GasStationSettings() {
  const { id: orgId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation(['admin', 'common']);
  const { station, organization, refreshStations } = useGasStation();

  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [saving, setSaving] = useState(false);

  const [name, setName] = useState(station?.name || '');
  const [addressData, setAddressData] = useState<AddressData | null>(null);
  const [loadingAddress, setLoadingAddress] = useState(false);

  // Logo state - compute URL from logoFileId
  const [logoUrl, setLogoUrl] = useState<string | null>(
    getImageUrl(station?.logoFileId)
  );
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoError, setLogoError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Sync name and logo with station
  useEffect(() => {
    if (station) {
      setName(station.name);
      setLogoUrl(getImageUrl(station.logoFileId));
    }
  }, [station]);

  // Fetch address if station has addressId
  useEffect(() => {
    async function fetchAddress() {
      if (!orgId || !station?.addressId) {
        setAddressData(null);
        return;
      }

      setLoadingAddress(true);
      try {
        const result = await getAddress({
          path: { org_id: orgId, id: station.addressId },
        });
        if (result.data?.data) {
          const address = result.data.data;
          setAddressData({
            formattedAddress: address.formattedAddress,
            address,
          });
        }
      } catch (err) {
        console.error('Failed to fetch address:', err);
      } finally {
        setLoadingAddress(false);
      }
    }

    fetchAddress();
  }, [orgId, station?.addressId]);

  const handleUpdate = async () => {
    if (!orgId || !station || !name) return;

    setSaving(true);
    const result = await updateGasStation({
      path: { org_id: orgId, id: station.id },
      body: {
        name,
        addressId: addressData?.address?.id || null,
      },
    });

    if (result.error) {
      console.error('Failed to update gas station:', result.error);
      setSaving(false);
      return;
    }

    setSaving(false);
    refreshStations();
  };

  const handleDelete = async () => {
    if (!orgId || !station) return;

    const result = await deleteGasStation({
      path: { org_id: orgId, id: station.id },
    });

    if (result.error) {
      console.error('Failed to delete gas station:', result.error);
      return;
    }

    setIsDeleteOpen(false);
    setDeleteConfirmText('');
    navigate(`/organizations/${orgId}`);
    refreshStations();
  };

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !orgId || !station) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setLogoError(t('gasStation.logoMustBeImage'));
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setLogoError(t('gasStation.logoTooLarge'));
      return;
    }

    setUploadingLogo(true);
    setLogoError(null);

    try {
      // Step 1: Get presigned URL from backend
      const uploadUrlResult = await getLogoUploadUrl({
        path: { org_id: orgId, id: station.id },
        body: {
          fileType: 'gas_station_logo',
          referenceId: station.id,
          contentType: file.type,
          filename: file.name,
          fileSizeBytes: file.size,
        },
      });

      if (uploadUrlResult.error) {
        const errorResponse = uploadUrlResult.error as ErrorResponse;
        throw new Error(errorResponse.error?.message || t('gasStation.logoUploadFailed'));
      }

      const { uploadId, uploadUrl } = uploadUrlResult.data!.data;

      // Step 2: Upload file directly to S3 using presigned URL
      // Must include x-amz-acl header since it's part of the presigned signature
      const s3Response = await fetch(uploadUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': file.type,
          'x-amz-acl': 'public-read',
        },
        body: file,
      });

      if (!s3Response.ok) {
        throw new Error(t('gasStation.logoUploadFailed'));
      }

      // Step 3: Confirm the upload with backend
      const confirmResult = await confirmLogoUpload({
        path: { org_id: orgId, id: station.id, upload_id: uploadId },
        body: {
          fileSizeBytes: file.size,
        },
      });

      if (confirmResult.error) {
        const errorResponse = confirmResult.error as ErrorResponse;
        throw new Error(errorResponse.error?.message || t('gasStation.logoUploadFailed'));
      }

      // Use getImageUrl to build full API URL from the upload ID
      setLogoUrl(getImageUrl(confirmResult.data?.data.upload.id));
      refreshStations();
    } catch (err) {
      setLogoError(err instanceof Error ? err.message : t('gasStation.logoUploadFailed'));
    } finally {
      setUploadingLogo(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleLogoDelete = async () => {
    if (!orgId || !station) return;

    setUploadingLogo(true);
    setLogoError(null);

    try {
      const result = await deleteGasStationLogo({
        path: { org_id: orgId, id: station.id },
      });

      if (result.error) {
        const errorResponse = result.error as ErrorResponse;
        throw new Error(errorResponse.error?.message || t('gasStation.logoDeleteFailed'));
      }

      setLogoUrl(null);
      refreshStations();
    } catch (err) {
      setLogoError(err instanceof Error ? err.message : t('gasStation.logoDeleteFailed'));
    } finally {
      setUploadingLogo(false);
    }
  };

  if (!station) {
    return null;
  }

  return (
    <>
      <div className="max-w-2xl">
        <div className="space-y-6">
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">{t('gasStation.generalInformation')}</h3>

            <div className="space-y-2">
              <Label htmlFor="settings-name">{t('gasStation.nameRequired')}</Label>
              <Input
                id="settings-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('gasStation.stationNamePlaceholder')}
              />
            </div>

            <div className="space-y-2">
              <Label>{t('gasStation.location')}</Label>
              {loadingAddress ? (
                <div className="p-3 border rounded-md bg-muted/50 text-sm text-muted-foreground">
                  {t('common:loading')}
                </div>
              ) : (
                <AddressInput
                  organizationId={orgId || ''}
                  value={addressData}
                  onChange={setAddressData}
                  defaultCountry={organization.default_country}
                />
              )}
            </div>

            <div className="space-y-2">
              <Label>{t('gasStation.logo')}</Label>
              <p className="text-sm text-muted-foreground">
                {t('gasStation.logoDescription')}
              </p>
              <div className="flex items-start gap-4">
                {/* Logo preview */}
                <div className="relative w-24 h-24 border rounded-lg overflow-hidden bg-muted flex items-center justify-center">
                  {logoUrl ? (
                    <>
                      <img
                        src={logoUrl}
                        alt={t('gasStation.logoAlt')}
                        className="w-full h-full object-contain"
                      />
                      {!uploadingLogo && (
                        <button
                          onClick={handleLogoDelete}
                          className="absolute top-1 right-1 p-1 bg-destructive text-destructive-foreground rounded-full hover:bg-destructive/90 transition-colors"
                          title={t('gasStation.removeLogo')}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      )}
                    </>
                  ) : (
                    <ImageIcon className="w-8 h-8 text-muted-foreground" />
                  )}
                  {uploadingLogo && (
                    <div className="absolute inset-0 bg-background/80 flex items-center justify-center">
                      <Loader2 className="w-6 h-6 animate-spin" />
                    </div>
                  )}
                </div>

                {/* Upload button */}
                <div className="space-y-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="hidden"
                    id="logo-upload"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingLogo}
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {logoUrl ? t('gasStation.changeLogo') : t('gasStation.uploadLogo')}
                  </Button>
                  <p className="text-xs text-muted-foreground">
                    {t('gasStation.logoHint')}
                  </p>
                  {logoError && (
                    <p className="text-xs text-destructive">{logoError}</p>
                  )}
                </div>
              </div>
            </div>

            <Button onClick={handleUpdate} disabled={!name || saving}>
              {saving ? t('common:buttons.saving') : t('gasStation.saveChanges')}
            </Button>
          </div>

          <hr className="my-8" />

          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-destructive">{t('gasStation.dangerZone')}</h3>
            <p className="text-sm text-muted-foreground">
              {t('gasStation.deleteWarning')}
            </p>
            <Button variant="destructive" onClick={() => setIsDeleteOpen(true)}>
              {t('gasStation.deleteGasStation')}
            </Button>
          </div>
        </div>
      </div>

      <AlertDialog open={isDeleteOpen} onOpenChange={(open) => {
        setIsDeleteOpen(open);
        if (!open) setDeleteConfirmText('');
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('gasStation.deleteConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription className="space-y-4">
              <p>
                <Trans
                  i18nKey="admin:gasStation.deleteConfirmMessage"
                  values={{ name: station.name }}
                  components={{ strong: <strong /> }}
                />
              </p>
              <p>
                <Trans
                  i18nKey="admin:gasStation.deleteConfirmPrompt"
                  values={{ name: station.name }}
                  components={{ strong: <strong /> }}
                />
              </p>
              <Input
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder={t('gasStation.typeToConfirm')}
              />
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common:buttons.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleteConfirmText !== station.name}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('common:buttons.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
